import { useMemo } from "react";

import { useAuthContext } from "~/v2/providers/AuthProvider";
import { queryKeys } from "../queryKeys";
import { getQuotes, type Quote, type StockLatestFeed } from "../rest/data";
import useRequest, { type QueryOptions } from "./useRequest";

type UseGetQuotesArgs = {
  symbols: string[];
  options?: QueryOptions<Quote[]>;
  feed?: StockLatestFeed;
};

export const useGetQuotes = ({ symbols, options, feed }: UseGetQuotesArgs) => {
  const { isAuthenticated } = useAuthContext();

  const sortedSymbols = useMemo(() => [...symbols].sort(), [symbols]);

  const {
    data: quotes,
    refetch,
    isFetched,
    isLoading,
    isFetching,
  } = useRequest(
    queryKeys.quotes.batch(sortedSymbols, feed),
    () => getQuotes(sortedSymbols, feed),
    {
      ...options,
      // Quotes run on the authed instance; firing them while logged out throws
      // in the request interceptor and surfaces as an unhandled rejection.
      enabled:
        isAuthenticated &&
        sortedSymbols.length > 0 &&
        (options?.enabled ?? true),
      refetchInterval: false,
    },
  );

  return { quotes, refetch, isFetched, isLoading, isFetching };
};

export default useGetQuotes;
